import type { EmailBlockData } from '../ModernEmailBuilder';
import type { StorageFile } from '../../../types/storage';
import { generateId } from './helpers';

const HEADER_BG_COLORS = [
  '#F8FAFC', // Slate 50
  '#F1F5F9', // Slate 100
  '#F0F9FF', // Sky 50
  '#EFF6FF', // Blue 50
  '#F5F3FF', // Violet 50
  '#FAF5FF', // Purple 50
  '#FDF2F8', // Pink 50
  '#FFF1F2', // Rose 50
  '#FFF7ED', // Orange 50
  '#FFFBEB', // Amber 50
  '#FEFCE8', // Yellow 50
  '#F0FDF4', // Green 50
  '#ECFDF5', // Emerald 50
  '#F0FDFA', // Teal 50
  '#1E293B', // Slate 800 (Dark)
  '#0F172A', // Slate 900 (Dark)
  '#111827', // Gray 900 (Dark)
  '#000000', // Black
];

export const getRandomHeaderColor = () => {
  return HEADER_BG_COLORS[Math.floor(Math.random() * HEADER_BG_COLORS.length)];
};

const RANDOM_LOGOS = [
  '/images/Email Builder/Blue Everest.png',
  '/images/Email Builder/Calypso.png',
  '/images/Email Builder/Cold Gecko.png',
  '/images/Email Builder/EdgeKart.png',
  '/images/Email Builder/Grasshopper.png',
  '/images/Email Builder/Harvest.png',
  '/images/Email Builder/HighCountryClub.png',
  '/images/Email Builder/InfinityParker.png',
  '/images/Email Builder/Kinetic.png',
  '/images/Email Builder/MajentaMantis.png',
  '/images/Email Builder/OmegaMillion.png',
  '/images/Email Builder/Pacific Trim.png',
  '/images/Email Builder/Pastel&Co..png',
  '/images/Email Builder/Wheelapp.png',
  '/images/Email Builder/Yellow Book.png'
];

export const getRandomLogo = () => {
  return RANDOM_LOGOS[Math.floor(Math.random() * RANDOM_LOGOS.length)];
};

export function createBlockFromType(type: string): EmailBlockData {
  const normalizedType = type === 'paragraph' ? 'text' : type;
  const baseBlock: EmailBlockData = {
    id: generateId(),
    type: normalizedType,
    content: {},
    styles: {},
  };

  switch (normalizedType) {
    case 'text':
      return {
        ...baseBlock,
        content: {
          text: 'This is a text block. Click to edit.',
        },
        styles: {
          padding: '20px',
          fontSize: '16px',
          lineHeight: '150%',
          color: '#111827',
          textAlign: 'left',
        },
      };
    case 'heading':
      return {
        ...baseBlock,
        content: {
          text: 'Heading',
          level: 1,
        },
        styles: {
          padding: '20px',
          fontSize: '32px',
          fontWeight: '700',
          color: '#111827',
          textAlign: 'left',
        },
      };
    case 'title':
      return {
        ...baseBlock,
        content: {
          text: 'Display Title',
          level: 1,
        },
        styles: {
          padding: '30px 20px 20px 20px',
          fontSize: '48px',
          fontWeight: '800',
          color: '#0F172A',
          textAlign: 'left',
          letterSpacing: '-0.025em',
          lineHeight: '110%',
        },
      };
    case 'subtitle':
      return {
        ...baseBlock,
        content: {
          text: 'Supporting subtitle text',
          level: 2,
        },
        styles: {
          padding: '10px 20px 20px 20px',
          fontSize: '20px',
          fontWeight: '500',
          color: '#64748B',
          textAlign: 'left',
          lineHeight: '150%',
        },
      };
    case 'quote':
      return {
        ...baseBlock,
        content: {
          text: '"A beautiful quote to inspire your readers and add visual interest to your email."',
          author: '— Author Name',
        },
        styles: {
          padding: '30px 40px',
          fontSize: '20px',
          fontWeight: '400',
          color: '#374151',
          textAlign: 'left',
          fontStyle: 'italic',
          lineHeight: '160%',
          borderLeft: '4px solid #3B82F6',
          backgroundColor: '#F8FAFC',
        },
      };
    case 'caption':
      return {
        ...baseBlock,
        content: {
          text: 'Caption text for images or descriptions',
        },
        styles: {
          padding: '8px 20px',
          fontSize: '12px',
          fontWeight: '400',
          color: '#9CA3AF',
          textAlign: 'left',
          lineHeight: '140%',
        },
      };
    case 'preheader':
      return {
        ...baseBlock,
        content: {
          text: 'Preview text that appears in email inbox before opening',
        },
        styles: {
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: '400',
          color: '#6B7280',
          textAlign: 'left',
          lineHeight: '150%',
          display: 'none', // Hidden in email but visible in preview
        },
      };
    case 'list':
      return {
        ...baseBlock,
        content: {
          items: [
            'First item in the list',
            'Second item in the list',
            'Third item in the list',
          ],
          listType: 'bullet', // 'bullet' | 'number' | 'check'
        },
        styles: {
          padding: '20px',
          fontSize: '16px',
          color: '#374151',
          textAlign: 'left',
          lineHeight: '180%',
        },
      };
    case 'image':
      return {
        ...baseBlock,
        content: {
          src: '',
          alt: 'Image',
          width: '100%',
          autoWidth: true,
          height: '300px',
          linkType: 'open-website',
          linkUrl: '',
          linkTarget: '_blank',
        },
        styles: {
          padding: '20px',
          textAlign: 'center',
          minHeight: '300px',
        },
      };
    case 'button':
      return {
        ...baseBlock,
        content: {
          text: 'Click Here',
          url: 'https://example.com',
          actionType: 'open-url',
          downloadFileUrl: '',
          downloadFileName: '',
          downloadFileSize: null,
          downloadFileId: null,
          downloadAs: '',
        },
        styles: {
          padding: '20px',
          textAlign: 'center',
          buttonStyle: {
            backgroundColor: '#3B82F6',
            color: '#FFFFFF',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            width: 'auto', // Default to auto width
          },
        },
      };
    case 'divider':
      return {
        ...baseBlock,
        content: {},
        styles: {
          padding: '20px',
          borderColor: '#E5E7EB',
          borderWidth: '1px',
          borderStyle: 'solid',
        },
      };
    case 'spacer':
      return {
        ...baseBlock,
        content: {
          height: '40px',
        },
        styles: {
          padding: '0px',
        },
      };
    case 'hero':
      return {
        ...baseBlock,
        content: {
          title: 'Welcome to Our Newsletter',
          subtitle: 'Stay updated with our latest news and offers',
          buttonText: 'Get Started',
          buttonUrl: '#',
          backgroundImage: '',
        },
        styles: {
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: '#F8FAFC',
        },
      };
    case 'columns':
      return {
        ...baseBlock,
        content: {
          columns: 2,
          columnContent: [
            [], // Empty array for column 1
            [], // Empty array for column 2
          ],
        },
        styles: {
          padding: '20px',
        },
      };
    case 'footer':
      return {
        ...baseBlock,
        content: {
          template: 'standard',
          text: '© 2024 Your Company. All rights reserved.',
          links: [
            { text: 'Privacy Policy', url: '#' },
          ],
        },
        styles: {
          padding: '40px 20px',
          backgroundColor: '#F8FAFC',
          textAlign: 'center',
          fontSize: '14px',
          color: '#6B7280',
        },
      };
    case 'video':
      return {
        ...baseBlock,
        content: {
          videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
          thumbnailUrl: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
          playButtonStyle: 'circle', // circle, square, none
        },
        styles: {
          padding: '20px',
          textAlign: 'center',
        },
      };
    case 'social':
      return {
        ...baseBlock,
        content: {
          platforms: [
            { name: 'Facebook', url: '#', icon: 'facebook' },
            { name: 'Twitter', url: '#', icon: 'twitter' },
            { name: 'Instagram', url: '#', icon: 'instagram' },
            { name: 'LinkedIn', url: '#', icon: 'linkedin' },
          ],
          iconSettings: {
            display: 'icon-only',
            iconStyle: 'solid',
            layout: 'horizontal',
            iconColor: '#000000',
            backgroundColor: 'transparent',
            alignment: 'center',
            gap: 12,
            size: 24,
          },
        },
        styles: {
          padding: '20px',
        },
      };
    case 'menu':
      return {
        ...baseBlock,
        content: {
          links: [
            { label: 'Home', url: '#' },
            { label: 'Products', url: '#' },
            { label: 'About', url: '#' },
            { label: 'Contact', url: '#' },
          ],
        },
        styles: {
          padding: '16px 20px',
          textAlign: 'center',
          color: '#334155',
          fontSize: '14px',
          fontWeight: '600',
          linkSpacing: '10px',
          buttonStyle: 'filled',
          backgroundColor: '#FFFFFF',
          containerBorderRadius: '0',
          itemBorderRadius: '0',
          itemPadding: '8px 14px',
          itemBorderColor: '#CBD5E1',
          itemBackgroundColor: '#F8FAFC',
          itemTextColor: '#334155',
        },
      };
    case 'logo':
      return {
        ...baseBlock,
        content: {
          src: getRandomLogo(),
          alt: 'Logo',
          width: '', // Let responsive sizing handle it
          linkType: 'open-website',
          linkUrl: '',
          linkTarget: '_blank',
          align: 'center',
        },
        styles: {
          padding: '20px',
          textAlign: 'center',
        },
      };
    case 'header':
      return {
        ...baseBlock,
        content: {
          layout: 'modern-clean', // modern-clean, bold-centered, action, dark-accent
          logoUrl: getRandomLogo(),
          logoMaxWidth: '120px',
          links: [
            { label: 'About', url: '#' },
            { label: 'Services', url: '#' },
            { label: 'Contact', url: '#' },
          ],
          navFontWeight: '600',
          navTextColor: '#1E293B',
          actionText: 'Get Started',
          actionUrl: '#',
        },
        styles: {
          padding: '20px',
          backgroundColor: getRandomHeaderColor(),
        },
      };

    case 'accordion':
      return {
        ...baseBlock,
        content: {
          items: [
            { title: 'Question 1', content: 'Answer to question 1' },
            { title: 'Question 2', content: 'Answer to question 2' },
          ],
        },
        styles: {
          padding: '20px',
          backgroundColor: '#FFFFFF',
        },
      };

    case 'image-carousel':
      return {
        ...baseBlock,
        content: {
          images: [
            { src: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80', alt: 'Mountain landscape' },
            { src: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=400&q=80', alt: 'Starry night mountains' },
            { src: 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?auto=format&fit=crop&w=400&q=80', alt: 'Orange flowers' },
          ],
        },
        styles: {
          padding: '10px',
          backgroundColor: '#FFFFFF',
        },
      };

    case 'giphy':
      return {
        ...baseBlock,
        content: {
          src: 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExM3R6eWJueDl5OGx5OGx5OGx5OGx5OGx5OGx5OGx5/3o7aD2saalBwwftBIY/giphy.gif',
          alt: 'GIF',
          width: '100%',
        },
        styles: {
          padding: '10px',
          textAlign: 'center',
        },
      };


    case 'creative-assistant':
      return {
        ...baseBlock,
        content: {
          prompt: 'Generate creative content',
          generated: false,
        },
        styles: {
          padding: '20px',
          backgroundColor: '#F0F9FF',
          border: '2px dashed #3B82F6',
        },
      };
    case 'survey':
      return {
        ...baseBlock,
        content: {
          title: 'Survey Question',
          questions: [{ id: '1', text: 'Question 1', type: 'multiple-choice' }],
        },
        styles: {
          padding: '20px',
          backgroundColor: '#F8FAFC',
        },
      };
    case 'code':
      return {
        ...baseBlock,
        content: {
          code: '<div>Custom HTML code</div>',
          language: 'html',
        },
        styles: {
          padding: '20px',
          backgroundColor: '#1F2937',
          color: '#F9FAFB',
          fontFamily: 'monospace',
        },
      };
    case 'apps':
      return {
        ...baseBlock,
        content: {
          apps: [],
        },
        styles: {
          padding: '20px',
        },
      };
    case 'product':
      return {
        ...baseBlock,
        content: {
          name: 'Product Name',
          description: 'Product description',
          price: '$99.99',
          oldPrice: '$129.99',
          image: 'https://via.placeholder.com/300x300',
          url: '#',
          buttonText: 'Buy Now',
        },
        styles: {
          padding: '20px',
          buttonStyle: {
            backgroundColor: '#10B981',
            color: '#FFFFFF',
            padding: '12px 32px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
          },
        },
      };
    case 'product-rec':
      return {
        ...baseBlock,
        content: {
          productsPerRow: 3,
          showRoundedCorners: false,
          products: [
            {
              name: 'Classic Watch',
              price: '$99.00',
              oldPrice: '$129.00',
              image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80',
              url: '#',
              buttonText: 'SHOP NOW',
            },
            {
              name: 'Premium Sunglasses',
              price: '$45.00',
              oldPrice: '$60.00',
              image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?auto=format&fit=crop&w=400&q=80',
              url: '#',
              buttonText: 'SHOP NOW',
            },
            {
              name: 'Leather Wallet',
              price: '$199.00',
              oldPrice: '$250.00',
              image: 'https://images.unsplash.com/photo-1618453292459-53424b66bb6a?auto=format&fit=crop&w=400&q=80',
              url: '#',
              buttonText: 'SHOP NOW',
            },
          ],
        },
        styles: {
          padding: '40px 24px',
          backgroundColor: '#FFFFFF',
          // Font settings for name
          productNameFontSize: '16px',
          productNameFontWeight: '600',
          productNameFontFamily: 'Inter',
          productNameColor: '#111827',
          // Font settings for price
          productPriceFontSize: '14px',
          productPriceFontWeight: '700',
          productPriceColor: '#111827',
          productPriceFontFamily: 'Arial, sans-serif',
        },
      };
    case 'music-launch':
      return {
        ...baseBlock,
        content: {
          layout: 'dark', // 'dark' | 'light' | 'tour'
          coverImage: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?auto=format&fit=crop&w=1200&q=80',
          title: 'New single out now',
          artist: 'Artist Name',
          releaseType: 'Single', // Single, EP, Album
          releaseDate: '',
          description:
            'Announce your latest track or album with a bold hero, short description, and clear call-to-action.',
          primaryButtonText: 'Listen now',
          primaryButtonUrl: '#',
          secondaryButtonText: '',
          secondaryButtonUrl: '',
          showSecondaryButton: false,
          showDescription: true,
          showTracklist: true,
          showPlatforms: true,
          tracks: [
            { title: 'Track One', duration: '3:21', featured: '' },
            { title: 'Track Two', duration: '2:58', featured: '' },
            { title: 'Track Three', duration: '4:02', featured: '' },
          ],
          platforms: {
            spotifyUrl: '',
            appleMusicUrl: '',
            youtubeUrl: '',
            soundcloudUrl: '',
            bandcampUrl: '',
          },
        },
        styles: {
          padding: '0',
          backgroundColor: '#020617',
        },
      };
    case 'newsletter':
      return {
        ...baseBlock,
        content: {
          layout: 'classic',
          image:
            'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=1200&q=80',
          showImage: true,
          title: 'This week at your brand',
          intro:
            'A short intro paragraph welcoming subscribers and setting expectations for this edition.',
          showButton: false,
          buttonText: '',
          buttonUrl: '',
          showSections: true,
          sections: [
            {
              title: 'Story 1',
              summary: 'Add a short headline and 2–3 lines of summary.',
              enabled: true,
            },
            {
              title: 'Story 2',
              summary: 'Highlight another important update or announcement.',
              enabled: true,
            },
            {
              title: 'Story 3',
              summary: 'Share a tip, resource, or link that adds value.',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'countdown':
      // Default to 7 days from now
      const defaultTargetDate = new Date();
      defaultTargetDate.setDate(defaultTargetDate.getDate() + 7);
      return {
        ...baseBlock,
        content: {
          targetDate: defaultTargetDate.toISOString(),
          title: 'Limited Time Offer',
          layout: 'horizontal',
          showLabels: true,
          expiredMessage: 'The countdown has ended',
        },
        styles: {
          padding: '20px',
          textAlign: 'center',
          backgroundColor: 'transparent',
          fontSize: '32px',
          fontWeight: '700',
          color: '#111827',
          fontFamily: 'Arial, sans-serif',
        },
      };
    case 'event-announcement':
      return {
        ...baseBlock,
        content: {
          layout: 'standard', // 'standard' | 'webinar'
          heroImage: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1200&q=80',
          title: 'Join Us for an Exclusive Event',
          description: 'Save the date and join us for an unforgettable experience. Limited seats available.',
          buttonText: 'Register Now',
          buttonUrl: '#',
          showDetails: true,
          eventDate: 'Friday, October 15, 2024',
          eventTime: '6:00 PM - 9:00 PM',
          eventLocation: 'Grand Ballroom, Downtown',
          additionalInfo: 'Don\'t miss out on this exclusive opportunity to network, learn, and connect with industry leaders.',
          showSpeaker: false,
          speakerName: '',
          speakerRole: '',
          speakerImage: '',
          speakerBio: '',
        },
        styles: {
          padding: '0',
          backgroundColor: '#0F172A',
        },
      };
    case 'testimonial':
      return {
        ...baseBlock,
        content: {
          layout: 'single', // 'single' | 'grid'
          testimonials: [
            {
              quote: 'This product changed my workflow completely. Highly recommend!',
              author: 'Jane Smith',
              role: 'CEO',
              image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80',
              rating: 5,
              enabled: true,
            },
            {
              quote: 'Amazing service and support. Exceeded all expectations!',
              author: 'Alex Johnson',
              role: '',
              image: '',
              rating: 5,
              enabled: true,
            },
            {
              quote: 'Best investment I\'ve made this year. Highly recommend!',
              author: 'Maria Garcia',
              role: '',
              image: '',
              rating: 5,
              enabled: true,
            },
          ],
          sectionTitle: 'What Our Customers Say',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'pricing-table':
      return {
        ...baseBlock,
        content: {
          sectionTitle: 'Choose Your Plan',
          plans: [
            {
              name: 'Starter',
              price: '$29',
              period: 'month',
              description: 'Perfect for individuals',
              features: ['Feature 1', 'Feature 2', 'Feature 3'],
              buttonText: 'Get Started',
              buttonUrl: '#',
              highlighted: false,
              enabled: true,
            },
            {
              name: 'Professional',
              price: '$79',
              period: 'month',
              description: 'Best for teams',
              features: ['All Starter features', 'Feature 4', 'Feature 5', 'Priority support'],
              buttonText: 'Get Started',
              buttonUrl: '#',
              highlighted: true,
              enabled: true,
            },
            {
              name: 'Enterprise',
              price: '$199',
              period: 'month',
              description: 'For large organizations',
              features: ['All Professional features', 'Feature 6', 'Feature 7', 'Dedicated support'],
              buttonText: 'Contact Sales',
              buttonUrl: '#',
              highlighted: false,
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'features-grid':
      return {
        ...baseBlock,
        content: {
          layoutVariant: 'cards',
          sectionTitle: 'Why Choose Us',
          features: [
            {
              icon: '⚡',
              title: 'Fast & Reliable',
              description: 'Lightning-fast performance with 99.9% uptime guarantee.',
              enabled: true,
            },
            {
              icon: '🔒',
              title: 'Secure & Private',
              description: 'Enterprise-grade security to protect your data.',
              enabled: true,
            },
            {
              icon: '💡',
              title: 'Easy to Use',
              description: 'Intuitive interface that anyone can master quickly.',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'promo-banner':
      return {
        ...baseBlock,
        content: {
          layout: 'with-countdown', // 'with-countdown' | 'simple'
          heroImage: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&q=80',
          title: 'Limited Time Offer',
          description: 'Get 50% off your first purchase. Use code SAVE50 at checkout.',
          buttonText: 'Shop Now',
          buttonUrl: '#',
          showCountdown: true,
          countdownDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          countdownTitle: 'Offer Ends In',
        },
        styles: {
          padding: '0',
          backgroundColor: '#DC2626',
        },
      };
    case 'blog-preview':
      return {
        ...baseBlock,
        content: {
          image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1200&q=80',
          category: 'Latest Blog Post',
          title: '10 Tips to Improve Your Productivity',
          excerpt: 'Discover proven strategies to boost your productivity and achieve more in less time. Learn from industry experts and implement these tips today.',
          buttonText: 'Read More',
          buttonUrl: '#',
          showImage: true,
          showCategory: true,
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'faq-section':
      return {
        ...baseBlock,
        content: {
          sectionTitle: 'Frequently Asked Questions',
          faqs: [
            {
              question: 'How do I get started?',
              answer: 'Simply sign up for a free account and follow our quick setup guide. You\'ll be up and running in minutes.',
              enabled: true,
            },
            {
              question: 'What payment methods do you accept?',
              answer: 'We accept all major credit cards, PayPal, and bank transfers for enterprise plans.',
              enabled: true,
            },
            {
              question: 'Can I cancel anytime?',
              answer: 'Yes, you can cancel your subscription at any time. No questions asked, no hidden fees.',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'team-grid':
      return {
        ...baseBlock,
        content: {
          sectionTitle: 'Meet Our Team',
          members: [
            {
              name: 'John Doe',
              role: 'CEO & Founder',
              image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
            {
              name: 'Jane Smith',
              role: 'Head of Design',
              image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
            {
              name: 'Mike Johnson',
              role: 'Lead Developer',
              image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'hero-content':
      return {
        ...baseBlock,
        content: {
          template: 'standard',
          image: '',
          heading: 'Introduce your concept',
          text: 'Use this space to introduce subscribers to the topic of this newsletter.',
          buttonText: 'Button',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'image-content':
      return {
        ...baseBlock,
        content: {
          template: 'standard',
          image: '',
          subtitle: 'Subtitle',
          heading: 'Introduce your concept',
          text: 'Use this space to introduce subscribers to the topic of this newsletter.',
          buttonText: 'Button',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'feature-showcase':
      return {
        ...baseBlock,
        content: {
          template: 'standard',
          image: '',
          heading: 'Amazing Feature',
          text: 'Describe your feature in detail. Explain the benefits and how it helps your customers achieve their goals.',
          buttonText: 'Learn More',
          buttonUrl: '#',
        },
        styles: {
          padding: '50px 20px',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'cta-banner':
      return {
        ...baseBlock,
        content: {
          template: 'standard',
          heading: 'Ready to Get Started?',
          text: 'Join thousands of satisfied customers and start your journey today.',
          buttonText: 'Get Started Now',
          buttonUrl: '#',
        },
        styles: {
          padding: '60px 40px',
          backgroundColor: '#3B82F6',
        },
      };
    case 'profile-card':
      return {
        ...baseBlock,
        content: {
          template: 'standard',
          profileImage: '',
          name: 'KENZI BARNES',
          role: 'Campaign manager',
          eventLabel: 'EVENT',
          eventTitle: 'Guest Lecture',
          eventDescription: 'Write event description here.',
          buttonText: 'Save my spot',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'magic-text':
      return {
        ...baseBlock,
        content: {
          text: 'Use the AI Magic tool to write something amazing...',
          isMagic: true, // Marker for properties panel
          magicTopic: '',
          magicTone: 'professional',
        },
        styles: {
          padding: '20px',
          fontSize: '16px',
          lineHeight: '1.6',
          color: '#111827',
          textAlign: 'left',
        },
      };
    case 'poll':
      return {
        ...baseBlock,
        content: {
          question: 'How likely are you to recommend us?',
          options: [
            { label: 'Very Likely', url: '#' },
            { label: 'Neutral', url: '#' },
            { label: 'Unlikely', url: '#' },
          ],
          pollStyle: 'buttons', // buttons, list
        },
        styles: {
          padding: '20px',
          backgroundColor: '#FFFFFF',
          textAlign: 'center',
        },
      };
    case 'qr-code':
      return {
        ...baseBlock,
        content: {
          url: 'https://example.com',
          width: 150,
          alignment: 'center',
          foregroundColor: '#000000',
          backgroundColor: '#FFFFFF',
        },
        styles: {
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'star-rating':
      return {
        ...baseBlock,
        content: {
          rating: 5,
          maxRating: 5,
          size: 24,
          color: '#FBBF24', // Amber-400
          emptyColor: '#E5E7EB', // Gray-200
          alignment: 'center',
          url: '', // Base URL for feedback
        },
        styles: {
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
        },
      };
    case 'sticker':
      return {
        ...baseBlock,
        content: {
          src: '',
          alt: 'Sticker',
          stickerType: 'builtin', // 'builtin' | 'giphy' | 'badge'
          stickerName: '',
          width: '120px',
        },
        styles: {
          padding: '10px',
          textAlign: 'center',
        },
      };
    default:
      return {
        ...baseBlock,
        content: {
          text: 'This is a text block. Click to edit.',
        },
        styles: {
          padding: '20px',
          fontSize: '16px',
          lineHeight: '1.6',
          color: '#111827',
          textAlign: 'center',
        },
      };
  }
}

export function createBlockFromContentFile(file: StorageFile): EmailBlockData {
  if (file.type === 'media') {
    const block = createBlockFromType('video');
    return {
      ...block,
      content: {
        ...block.content,
        videoUrl: file.url,
        thumbnailUrl: file.thumbnailUrl || block.content.thumbnailUrl,
      },
    };
  }

  if (file.type === 'image' || file.type === 'graphic' || file.type === 'logo') {
    const block = createBlockFromType('image');
    return {
      ...block,
      content: {
        ...block.content,
        src: file.url,
        alt: file.originalName || 'Image',
        height: 'auto',
      },
      styles: {
        ...block.styles,
        minHeight: undefined,
      },
    };
  }

  const block = createBlockFromType('button');
  return {
    ...block,
    content: {
      ...block.content,
      text: file.originalName || 'Download file',
      actionType: 'download-file',
      downloadFileUrl: file.url,
      downloadFileName: file.originalName || file.name,
      downloadFileSize: file.size,
      downloadFileId: file.id,
      downloadAs: file.originalName || file.name,
      url: file.url,
    },
  };
}
